import { Call } from "@twilio/voice-sdk";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  removeCallFromQueueAction,
  selectDialerActiveCall,
  selectDialerIncomingCall,
  selectExecuteCallUpdateSid,
  selectIgnoreCallSids,
  updateCreateCallDetailsAction,
} from "components/suno-dialer/suno-dialer-slice";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useUpdateCallEventMutation } from "services/suno-comms/suno-comms-api";

interface CurrentCallContextValue {
  currentCall: Call | null;
  setCurrentCall: (call: Call | null) => void;
  lastCallSid: string | undefined;
  setLastCallSid?: React.Dispatch<React.SetStateAction<string | undefined>>;
  shouldIgnore?: (sid: string) => boolean;
}

const CurrentCallContext = createContext<CurrentCallContextValue>({
  currentCall: null,
  setCurrentCall: () => {},
  lastCallSid: "",
  setLastCallSid: undefined,
  shouldIgnore: undefined,
});

export const CurrentCallContextProvider = ({ children }: any) => {
  const dispatch = useAppDispatch();
  const [updateCallEvent] = useUpdateCallEventMutation();

  const [currentCall, setCurrentCall] = useState<Call | null>(null);

  const [lastCallSid, setLastCallSid] = useState<string | undefined>();

  const ignoreList = useAppSelector(selectIgnoreCallSids);
  const shouldIgnore = useCallback(
    (sid: string) => {
      return Boolean(ignoreList && ignoreList[sid]);
    },
    [ignoreList]
  );

  const { incomingCall } = useAppSelector(selectDialerIncomingCall);

  const { activeCall } = useAppSelector(selectDialerActiveCall);

  useEffect(() => {
    // Fetch the last call's status once after the call is dismissed
    if (lastCallSid) {
      const timer = setTimeout(() => {
        setLastCallSid(undefined);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [lastCallSid]);

  const executeCallUpdateSid = useAppSelector(selectExecuteCallUpdateSid);

  useEffect(() => {
    if (executeCallUpdateSid) {
      updateCallEvent({
        callSid: executeCallUpdateSid,
        status: "answered",
      });
      dispatch(
        updateCreateCallDetailsAction({
          callSid: "",
          isCreateExecuted: false,
          isShouldExecuteUpdate: false,
        })
      );
    }
  }, [dispatch, executeCallUpdateSid, updateCallEvent]);

  useEffect(() => {
    if (
      !activeCall?.id &&
      incomingCall?.id &&
      shouldIgnore?.(incomingCall?.id)
    ) {
      dispatch(removeCallFromQueueAction(incomingCall?.id));
      currentCall?.disconnect();
      setCurrentCall(null);
      setLastCallSid(undefined);
    }
  }, [
    activeCall?.id,
    currentCall,
    dispatch,
    incomingCall?.id,
    setCurrentCall,
    shouldIgnore,
  ]);

  const result: CurrentCallContextValue = {
    currentCall,
    setCurrentCall,
    lastCallSid,
    setLastCallSid,
    shouldIgnore,
  };

  return (
    <CurrentCallContext.Provider value={result}>
      {children}
    </CurrentCallContext.Provider>
  );
};

export const useCallContext = () => {
  const context = useContext(CurrentCallContext);
  if (!context) {
    throw new Error(
      `useCallContext must be used within a CurrentCallContextProvider`
    );
  }
  return context;
};
